import { useEffect, useRef, useState } from "react"
import styles from "./Select.module.css"

interface Option {
  value: string
  label: string
}

interface Props {
  value: string
  options: Option[]
  onChange: (value: string) => void
  ariaLabel?: string
}

export function Select({ value, options, onChange, ariaLabel }: Props) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const selected = options.find((o) => o.value === value)

  useEffect(() => {
    if (!open) return
    function onPointerDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("mousedown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
      >
        <span>{selected?.label ?? ""}</span>
        <span className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`} aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        // biome-ignore lint/a11y/useSemanticElements: custom dropdown, not a native <select>
        <div className={styles.menu} role="listbox" tabIndex={-1}>
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              // biome-ignore lint/a11y/useSemanticElements: <option> only renders inside <select>
              role="option"
              aria-selected={opt.value === value}
              className={`${styles.option} ${opt.value === value ? styles.optionSelected : ""}`}
              onClick={() => {
                onChange(opt.value)
                setOpen(false)
              }}
            >
              <span className={styles.check} aria-hidden="true">
                {opt.value === value ? "✓" : ""}
              </span>
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
